import { API_ROUTES } from '@/constants/apiRoutes';
import { ApiResponse, PaginatedApiResponse } from '@/react-query/context/ApiClientContextController';
import { queryFactoryOptions } from '@/react-query/utills/queryFactoryOptions';
import { AxiosInstance } from 'axios';
import {
  CustomerPointSettingListResponse,
  CustomerReferralStageDetail,
  CustomerReferralStageList,
  PointCategoryDetails,
  PointConfigurationData,
  SlabsData,
} from './pointConfiguration.types';

export interface PointListParams {
  agentType: string;
  row?: number;
  fetch?: number;
  search?: string;
  fromDate?: number;
  toDate?: number;
}

export interface PointSlabListParams extends PointListParams {
  pointCategoryId: string;
}

export interface CustomerPointSettingListParams {
  search?: string;
  fromDateMs?: number;
  toDateMs?: number;
  row: number;
  fetch: number;
}

export interface CustomerPointSettingDetailParams {
  programId: string;
}

export interface CustomerReferralStageListParams extends CustomerPointSettingListParams {
  programId: string;
}

export interface CustomerReferralStageDetailParams {
  stageId: string;
}

const getPointList = (client: AxiosInstance) => async (params: PointListParams) =>
  (await client.get<PaginatedApiResponse<PointConfigurationData[]>>(API_ROUTES.GET_POINTS_LIST, { params })).data;
const getPointDetails = (client: AxiosInstance) => async (pointCategoryId: string) =>
  (
    await client.get<ApiResponse<PointCategoryDetails>>(API_ROUTES.GET_POINT_CATEGORY_DETAILS, {
      params: { pointCategoryId },
    })
  ).data;
const getSlabsList = (client: AxiosInstance) => async (params: PointSlabListParams) =>
  (await client.get<PaginatedApiResponse<SlabsData[]>>(API_ROUTES.GET_POINT_SLABS, { params })).data;
const getSlabDetails = (client: AxiosInstance) => async (pointSlabId: string) =>
  (await client.get<ApiResponse<SlabsData>>(API_ROUTES.GET_POINT_SLAB_DETAILS, { params: { pointSlabId } })).data;

const getCustomerPointSettingList = (client: AxiosInstance) => async (params: CustomerPointSettingListParams) =>
  (
    await client.get<PaginatedApiResponse<CustomerPointSettingListResponse[]>>(API_ROUTES.GET_POINT_SETTING_LIST, {
      params,
    })
  ).data;

const getCustomerPointSettingDetail = (client: AxiosInstance) => async (params: CustomerPointSettingDetailParams) =>
  (await client.get<ApiResponse<CustomerPointSettingListResponse>>(API_ROUTES.GET_POINT_SETTING_DETAIL, { params }))
    .data;

const getCustomerReferralStageList = (client: AxiosInstance) => async (params: CustomerReferralStageListParams) =>
  (await client.get<PaginatedApiResponse<CustomerReferralStageList[]>>(API_ROUTES.GET_REFERRAL_STAGE_LIST, { params }))
    .data;

const getCustomerReferralStageDetail = (client: AxiosInstance) => async (params: CustomerReferralStageDetailParams) =>
  (
    await client.get<ApiResponse<CustomerReferralStageDetail>>(API_ROUTES.GET_REFERRAL_STAGE_DETAIL, {
      params,
    })
  ).data;

export const pointConfigurationQueries = {
  all: () => ['point-management'],
  list: (params: PointListParams) =>
    queryFactoryOptions<PaginatedApiResponse<PointConfigurationData[]>>({
      queryKey: [...pointConfigurationQueries.all(), 'list', params],
      queryFn: (client) => () => getPointList(client)(params),
    }),
  details: (pointCategoryId: string) =>
    queryFactoryOptions<ApiResponse<PointCategoryDetails>>({
      queryKey: [...pointConfigurationQueries.all(), 'details', pointCategoryId],
      queryFn: (client) => () => getPointDetails(client)(pointCategoryId),
    }),
};

export const SlabsQueries = {
  all: () => [...pointConfigurationQueries.all(), 'slabs'],
  list: (params: PointSlabListParams) =>
    queryFactoryOptions<PaginatedApiResponse<SlabsData[]>>({
      queryKey: [...SlabsQueries.all(), 'list', params],
      queryFn: (client) => () => getSlabsList(client)(params),
    }),
  details: (pointSlabId: string) =>
    queryFactoryOptions<ApiResponse<SlabsData>>({
      queryKey: [...SlabsQueries.all(), 'details', pointSlabId],
      queryFn: (client) => () => getSlabDetails(client)(pointSlabId),
    }),
};

export const customerPointSettingQueries = {
  all: () => ['customerPointSetting'],
  list: (params: CustomerPointSettingListParams) =>
    queryFactoryOptions<PaginatedApiResponse<CustomerPointSettingListResponse[]>>({
      queryKey: [...customerPointSettingQueries.all(), 'list', params],
      queryFn: (client) => () => getCustomerPointSettingList(client)(params),
    }),
  detail: (params: CustomerPointSettingDetailParams) =>
    queryFactoryOptions<ApiResponse<CustomerPointSettingListResponse>>({
      queryKey: [...customerPointSettingQueries.all(), 'detail', params],
      queryFn: (client) => () => getCustomerPointSettingDetail(client)(params),
    }),
};

export const customerReferralStageQueries = {
  all: () => [...customerPointSettingQueries.all(), 'referralStage'],
  list: (params: CustomerReferralStageListParams) =>
    queryFactoryOptions<PaginatedApiResponse<CustomerReferralStageList[]>>({
      queryKey: [...customerReferralStageQueries.all(), 'list', params],
      queryFn: (client) => () => getCustomerReferralStageList(client)(params),
    }),
  detail: (params: CustomerReferralStageDetailParams) =>
    queryFactoryOptions<ApiResponse<CustomerReferralStageDetail>>({
      queryKey: [...customerReferralStageQueries.all(), 'detail', params],
      queryFn: (client) => () => getCustomerReferralStageDetail(client)(params),
    }),
};
